import { useCallback, useMemo, useState } from "react";
import { type DropdownProps } from "semantic-ui-react";

import { useToggle } from "hooks/useToggle";
import Dropdown from "features/Library/Common/Fields/Dropdown";
import { ValidationThrottler } from "features/Library/Common/Fields/ValidationThrottler";
import { WithFormikPayload } from "features/Library/Common/Fields/WithFormikPayload";
import { type QuestionTypeOptions } from "features/Library/Common/models";
import { type QuestionHeaderPropsBase } from "features/Library/Common/props";
import { MapQuestion } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyProviders/QuestionMapper";
import { type SurveyQuestion } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/types";
import { SurveyQuestionType } from "features/Library/Surveys/types/models";
import {
  isAllowedBranchingQuestionType,
  nameofWithPrefix,
} from "features/Library/Surveys/SurveyDetails/SurveyContentForm/utils";

import { BranchingToggle } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyHeader/BranchingToggle/BranchingToggle";
import { RequiredToggle } from "features/Library/Surveys/SurveyDetails/SurveyContentForm/SurveyHeader/RequiredToggle/RequiredToggle";
import { QuestionHeader } from "features/Library/Common/QuestionsForm/Questions/QuestionHeader/QuestionHeader";
import { MIN_QUESTIONS_LENGTH } from "features/Library/Common/constants";

export type SurveyQuestionHeaderProps = QuestionHeaderPropsBase<SurveyQuestion<any>>;

const options: Array<QuestionTypeOptions> = [
  { text: "Multiple Choice Text (multi-select)", value: SurveyQuestionType.MultipleChoiceTextQuestion },
  { text: "Multiple Choice Text (single-select)", value: SurveyQuestionType.SingleChoiceTextQuestion },
  { text: "Rating Scale", value: SurveyQuestionType.RatingScale },
  { text: "Free Text", value: SurveyQuestionType.FreeText },
];

export const SurveyQuestionHeader = (props: SurveyQuestionHeaderProps) => {
  const {
    question,
    validatedFieldProps,
    index,
    onChanged,
    disabled,
    onQuestionTypeChanged,
    propertyNamePrefix,
    onBlur,
    onRemove,
  } = props;

  const [showMapperError, toggleShowMapperError] = useToggle();
  const [previousQuestion, setPreviousQuestion] = useState<SurveyQuestion<any> | undefined>(undefined);

  const nameof = useMemo(() => {
    return nameofWithPrefix<SurveyQuestion<any>>(propertyNamePrefix);
  }, [propertyNamePrefix]);

  const onCheckboxChanged = useCallback(() => {
    if (showMapperError) {
      toggleShowMapperError();
      setPreviousQuestion(undefined);
    }
    onChanged();
  }, [showMapperError, onChanged, toggleShowMapperError, setPreviousQuestion]);

  const handleTypeDropdownChange = (_: any, data: DropdownProps) => {
    const value = data.value as SurveyQuestionType;
    const questionToMap = previousQuestion ?? question;
    const mapResult = MapQuestion(value, questionToMap);

    if (!mapResult.success) {
      toggleShowMapperError();
      setPreviousQuestion(questionToMap);
    }

    onQuestionTypeChanged(mapResult.question);
  };

  const { setFieldValue } = validatedFieldProps;
  const { isBranching } = question;

  const setDropdownValue = useCallback(
    async (field: string, value: SurveyQuestionType, shouldValidate?: boolean) => {
      await setFieldValue(field, value, shouldValidate);
      if (!isAllowedBranchingQuestionType(value) && isBranching) {
        await setFieldValue(nameof("isBranching"), false, shouldValidate);
      }
    },
    [isBranching, setFieldValue, nameof],
  );

  const togglePropertiesBase = {
    onBlur,
    propertyNamePrefix,
    validatedFieldProps,
    onChanged: onCheckboxChanged,
    question,
    disabled,
  };

  const handleRemove = () => onRemove(index);

  const isBranchingAllowed = useMemo(
    () => isAllowedBranchingQuestionType(question.questionType),
    [question.questionType],
  );

  return (
    <>
      {showMapperError && (
        <div className="mapping-error" style={{ color: "red" }}>
          ERROR
        </div>
      )}
      <QuestionHeader
        title={`Question ${index + 1}`}
        canRemove={!disabled && index >= MIN_QUESTIONS_LENGTH}
        onRemove={handleRemove}
      >
        {isBranchingAllowed && <BranchingToggle {...togglePropertiesBase} index={index} />}
        <RequiredToggle {...togglePropertiesBase} />
      </QuestionHeader>

      <WithFormikPayload
        {...validatedFieldProps}
        propertyName={nameof("questionType")}
        pathToValidate={`questions.${index}`}
        selfValidation
        render={(formikProps) => (
          <ValidationThrottler {...formikProps} value={question.questionType}>
            <Dropdown
              {...formikProps}
              label="Question Type"
              value={question.questionType}
              options={options}
              errorPosition={"bottom"}
              disabled={disabled}
              setFieldTouched={validatedFieldProps.setFieldTouched}
              setFieldValue={setDropdownValue}
              onChange={handleTypeDropdownChange}
              disableValidation
            />
          </ValidationThrottler>
        )}
      />
    </>
  );
};
